import { DOCUMENT } from "@angular/common";
import { DestroyRef, inject, Injectable, signal } from "@angular/core";

@Injectable()
export class HomeExternalDrawer {
	private readonly document = inject(DOCUMENT);
	private readonly destroyRef = inject(DestroyRef);

	private readonly _open = signal(false);

	readonly open = this._open.asReadonly();

	private prevBodyOverflow = "";
	private prevBodyPaddingRight = "";
	private prevHtmlOverflow = "";

	constructor() {
		const onResize = (): void => {
			if (this.document.defaultView && this.document.defaultView.innerWidth > 768 && this._open()) {
				this.close();
			}
		};
		this.document.defaultView?.addEventListener("resize", onResize, { passive: true });
		this.destroyRef.onDestroy(() => {
			this.document.defaultView?.removeEventListener("resize", onResize);
			this.unlockBodyScroll();
		});
	}

	open$(): void {
		if (this._open()) {
			return;
		}
		this._open.set(true);
		this.lockBodyScroll();
	}

	close(): void {
		if (!this._open()) {
			return;
		}
		this._open.set(false);
		this.unlockBodyScroll();
	}

	toggle(): void {
		if (this._open()) {
			this.close();
		} else {
			this.open$();
		}
	}

	private lockBodyScroll(): void {
		const body = this.document.body;
		const html = this.document.documentElement;
		const win = this.document.defaultView;
		if (!body || !html) {
			return;
		}

		this.prevBodyOverflow = body.style.overflow;
		this.prevBodyPaddingRight = body.style.paddingRight;
		this.prevHtmlOverflow = html.style.overflow;

		const scrollbarWidth = win ? win.innerWidth - html.clientWidth : 0;
		if (scrollbarWidth > 0) {
			body.style.paddingRight = `${scrollbarWidth}px`;
		}
		body.style.overflow = "hidden";
		html.style.overflow = "hidden";
	}

	private unlockBodyScroll(): void {
		const body = this.document.body;
		const html = this.document.documentElement;
		if (!body || !html) {
			return;
		}
		body.style.overflow = this.prevBodyOverflow;
		body.style.paddingRight = this.prevBodyPaddingRight;
		html.style.overflow = this.prevHtmlOverflow;
	}
}
